import React, { useCallback, useMemo, useState } from "react";
import { Box, Typography, Paper } from "@material-ui/core";
import { useSelectedTheme, useTranslate } from "../../../../../../../Hooks";
import { StaticLookupsIds } from "../../../../../../../assets/json/StaticLookupsIds";
import {
  CustomButton,
  CustomCard,
  CustomCopyToClipboard,
  CustomInput,
} from "../../../../../../../Components";
import NewPropertyMPIGaugeChartComponent from "./NewPropertyMPIGaugeChartComponent/NewPropertyMPIGaugeChartComponent";

// icons
import { PieChart01 } from "../../../../../../../assets/icons";

// styles
import useStyles from "./styles";

function MatchingCRMUnitsDialog({ selectedUnits, summaryData, onClose }) {
  const styles = useStyles();

  const { translate } = useTranslate("NewPropertiesView");
  const {
    theme: { palette },
  } = useSelectedTheme();

  const [searchValue, setSearchValue] = useState("");
  const [activeUnit, setActiveUnit] = useState(null);

  const filteredUnits = useMemo(
    () =>
      selectedUnits.filter((unit) =>
        unit.unitRefNo.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [selectedUnits, searchValue]
  );

  const handleUnitSelect = useCallback(
    (unit) => {
      setActiveUnit({
        id: unit.unitId,
        builtup_area_sqft: parseFloat(unit.builtup_area_sqft),
        medianPricePerSqft:
          summaryData.find((item) =>
            item.unitReferences.some((ref) => ref.unitRefNo === unit.unitRefNo)
          )?.medianPricePerSqft || 0,
        marketComparison: null,
      });
    },
    [summaryData]
  );

  const getOperationTypeId = (operationType) => {
    switch (operationType.toLowerCase()) {
      case "sales":
        return StaticLookupsIds.Sales;
      case "rent":
        return StaticLookupsIds.Rent;
      case "saleandrent":
        return StaticLookupsIds.SaleAndRent;
      default:
        return StaticLookupsIds.Sales;
    }
  };

  return (
    <Paper className={styles.modal}>
      <Box className={styles.header}>
        <CustomCard
          borderRadius="lg"
          borderColor="secondary"
          style={{ marginBottom: 16 }}
        >
          <NewPropertyMPIGaugeChartComponent
            marketComparison={activeUnit?.marketComparison || null}
            unitId={activeUnit?.id}
            activeData={activeUnit}
            operationType={getOperationTypeId(
              summaryData[0]?.operationType || "Sales"
            )}
          />
        </CustomCard>
      </Box>
      <Box className={styles.middleSection}>
        <Typography className={styles.mainTitle}>Unit list</Typography>
        <Typography className={styles.subTitle}>
          Browse all available units in one place
        </Typography>
        <Box className={styles.searchBox}>
          <CustomInput
            type="text"
            placeholder="Search"
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            debounceTime={1300}
            value={searchValue}
            CustomClass={styles.customInputContainer}
          />
        </Box>
      </Box>
      <Box className={styles.wrapperContent}>
        {filteredUnits.map((unit) => (
          <CustomCard
            key={unit.unitRefNo}
            borderRadius="lg"
            borderColor="secondary"
            style={{ marginBottom: 16 }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2}
              className={styles.card}
            >
              <Box display="flex" flexDirection="column" style={{ gap: 2 }}>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Typography className={styles.cardText}>
                    Unit reference number
                  </Typography>
                  <CustomCopyToClipboard
                    data={unit.unitRefNo} // copy
                    childrenData={unit.unitRefNo} // render
                    copyDoneShowTime={1000}
                    fontSize="14px"
                  />
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Typography className={styles.cardText}>
                    Built up area
                  </Typography>
                  <span
                    style={{
                      color: palette.text.primary,
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    {Number(unit.builtup_area_sqft) > 0
                      ? Number(unit.builtup_area_sqft) % 1 === 0
                        ? Number(unit.builtup_area_sqft).toLocaleString()
                        : Number(unit.builtup_area_sqft).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            }
                          )
                      : "N/A"}{" "}
                    sqft
                  </span>
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                style={{ gap: 8, cursor: "pointer" }}
                onClick={() => handleUnitSelect(unit)}
              >
                <PieChart01
                  width="20"
                  height="20"
                  fill={palette.button.tertiaryColor_fg}
                />
                <Typography
                  style={{
                    color: palette.button.tertiaryColor_fg,
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Show MPI chart
                </Typography>
              </Box>
            </Box>
          </CustomCard>
        ))}
      </Box>
      {/* footer action */}
      <Box className={styles.footerActions}>
        <CustomButton variant="outlined" color="secondary" onClick={onClose}>
          {translate("cancel")}
        </CustomButton>
      </Box>
    </Paper>
  );
}

export default MatchingCRMUnitsDialog;
